import React from 'react';
import { Link, Project, WeekData } from '../../StateContext';

import EditWeek from './editWeek';
import EditAllWeeksWeight from './editAllWeeksWeight';
import { useLanguage } from '../../Services/LanguageContext';
import './weekView.css';

interface IProps {
    link?: Link;
    weekData?: WeekData;
    project?: Project;
    filterTypeId?: number | null;
}

export const secondsToHours = (secondsWorked) => {
    return secondsWorked / 3600;
};

export const totalHours = (weekData: WeekData[]) => {
    let total = 0;
    for (const week of weekData) {
        const hours = secondsToHours(week.secondsWorked);
        const corrected = hourMultipliedByWeight(hours, week.weight);
        total += corrected;
    }
    return total;
};

export const totalProjectHours = (links: Link[]) => {
    let total = 0;
    for (const link of links) {
        total += totalHours(link.weekData);
    }
    return total;
};

export const hourMultipliedByWeight = (hoursWorked, weight: number) => {
    return (hoursWorked * weight) / 100;
};

export const totalHoursUpTo = (weekData: WeekData[], upToWeek: WeekData) => {
    let total = 0;
    for (const week of weekData) {
        if (week.weekNumber > upToWeek.weekNumber) continue;
        const hours = secondsToHours(week.secondsWorked);
        total += hourMultipliedByWeight(hours, week.weight);
    }
    return total;
};

// DHZ hours (customer helping mechanic) count as extra effort against the estimate
// dhzSeconds lives on the project, so pass it separately
export const totalProjectBaseline = (links: Link[], dhzSeconds = 0) => {
    return totalProjectHours(links) + secondsToHours(dhzSeconds);
};

export const hoursForType = (links: Link[], typeId: number) => {
    let total = 0;
    for (const link of links) {
        for (const week of link.weekData) {
            if (week.projectTypeId === typeId) {
                total += hourMultipliedByWeight(secondsToHours(week.secondsWorked), week.weight);
            }
        }
    }
    return total;
};

const typeLabel: React.CSSProperties = {
    display: 'inline-block',
    padding: '1px 7px',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: 600,
    background: '#1e293b',
    border: '1px solid #334',
    color: '#7dd3fc',
    whiteSpace: 'nowrap',
};

const WeekView = (props: IProps) => {
    const { t } = useLanguage();
    const sortWeeks = (a: WeekData, b: WeekData) => a.weekNumber - b.weekNumber;
    const projectTypes = props.project?.types ?? [];
    const hasTypes = projectTypes.length > 0 && props.filterTypeId === undefined;

    const getTypeName = (typeId?: number) => {
        if (!typeId) return null;
        return projectTypes.find((pt) => pt.id === typeId)?.name ?? null;
    };

    const displayWeeks = props.filterTypeId !== undefined
        ? props.link.weekData
            .filter(w => props.filterTypeId === null ? w.projectTypeId == null : w.projectTypeId === props.filterTypeId)
            .sort(sortWeeks)
        : props.link.weekData.slice().sort(sortWeeks);

    return (
        <div className='week-view-container'>
            <div className='week-view-header'>
                <div className='week-view-cell'>{t('week')}</div>
                <div className='week-view-cell'>{t('hours')}</div>
                <div className='week-view-cell week-view-percentage-header'>
                    {t('percentageToCount')}
                    {props.link && <EditAllWeeksWeight link={props.link} />}
                </div>
                {hasTypes && <div className='week-view-cell'>{t('workType')}</div>}
                <div className='week-view-cell'>{t('totalHours')}</div>
                <div className='week-view-cell'></div>
            </div>
            {displayWeeks.map((x, index) => (
                <div className='week-view-row' key={index}>
                    <div className='week-view-cell'>{x.weekNumber}</div>
                    <div className='week-view-cell'>
                        {secondsToHours(x.secondsWorked).toFixed(2)}{t('hourSuffix')}
                    </div>
                    <div className='week-view-cell'>
                        <span className='week-percentage-badge'>{x.weight}%</span>
                    </div>
                    {hasTypes && (
                        <div className='week-view-cell'>
                            {getTypeName(x.projectTypeId)
                                ? <span style={typeLabel}>{getTypeName(x.projectTypeId)}</span>
                                : <span style={{ color: '#555', fontSize: '11px' }}>—</span>
                            }
                        </div>
                    )}
                    <div className='week-view-cell week-total-hours'>
                        {totalHoursUpTo(displayWeeks, x).toFixed(2)}{t('hourSuffix')}
                    </div>
                    <div className='week-view-cell'>
                        <EditWeek weekData={x} link={props.link} />
                    </div>
                </div>
            ))}
        </div>
    );
};

export default WeekView;
